import { useState } from 'react'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Text,
  Stack,
} from '@chakra-ui/react'
import {
  DEFAULT_HISTORY_PAGE_SIZE,
  HISTORY_LOAD_MORE_INCREMENT,
  ACCORDION_BORDER_STYLE,
  ACCORDION_BUTTON_STYLE,
} from '../constants'

interface HistoryAccordionProps<T> {
  title: string
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  sortFn?: (a: T, b: T) => number
  initialDisplayCount?: number
  loadMoreIncrement?: number
  emptyMessage?: string
}

export function HistoryAccordion<T>({
  title,
  items,
  renderItem,
  sortFn,
  initialDisplayCount = DEFAULT_HISTORY_PAGE_SIZE,
  loadMoreIncrement = HISTORY_LOAD_MORE_INCREMENT,
  emptyMessage = `No ${title.toLowerCase()} available.`,
}: HistoryAccordionProps<T>) {
  const [displayCount, setDisplayCount] = useState(initialDisplayCount)

  if (!items || items.length === 0) return null

  const sortedItems = sortFn ? [...items].sort(sortFn) : items
  const visibleItems = sortedItems.slice(0, displayCount)
  const hasMore = sortedItems.length > displayCount

  function loadMore() {
    setDisplayCount((prev) => prev + loadMoreIncrement)
  }

  return (
    <Box mt={2}>
      <Accordion defaultIndex={[]} allowToggle>
        <AccordionItem {...ACCORDION_BORDER_STYLE}>
          <AccordionButton {...ACCORDION_BUTTON_STYLE}>
            <Box flex='1' textAlign='left'>
              <Text fontWeight='semibold'>{title}</Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0} pb={2}>
            {items.length === 0 ? (
              <Text fontSize='sm' color='gray.600' px={3}>
                {emptyMessage}
              </Text>
            ) : (
              <>
                <Stack spacing={1}>{visibleItems.map(renderItem)}</Stack>
                {hasMore && (
                  <Button
                    mt={2}
                    ml={0}
                    size='sm'
                    onClick={loadMore}
                    aria-label={`Load more ${title.toLowerCase()}`}
                  >
                    Load More ({sortedItems.length - displayCount} remaining)
                  </Button>
                )}
              </>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  )
}
